import React, { useMemo, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Backdrop,
  CircularProgress,
  Typography,
  IconButton,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import AssignmentIndOutlinedIcon from '@material-ui/icons/AssignmentIndOutlined';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import DatePicker from 'src/components/DatePicker';
import {
  useGestionMarcacionApi,
  RegistroMarcacion,
  CupoMarcacion,
  OperarioItem,
  UsuarioInfo,
} from '../hooks/useGestionMarcacionApi';
import CorregirMarcacionModal from './CorregirMarcacionModal';
import SuperaModal from './SuperaModal';
import { sumarHorasHHMM } from '../utils/marcacionUtils';
import { GestionErroresDetalleItem } from 'src/modules/gestionErrores/types';
import { agregarLpGestionado, listarLpGestionados } from 'src/modules/gestionErrores/gestionLpStorage';
import GestionSinAsignacionSelectorModal from 'src/modules/gestionErrores/components/GestionSinAsignacionSelectorModal';
import ReemplazoRapidoModal from 'src/modules/gestionErrores/components/ReemplazoRapidoModal';
import AsignarLimpiezaProfundaModal from 'src/modules/gestionErrores/components/AsignarLimpiezaProfundaModal';
import './PendientesPanel.css';

// Pestaña "Pendientes" de Gestión de Errores (antes módulo "Gestión Marcación (nuevo)"):
// lista plana de marcaciones sin procesar con filtros y resumen de cupos.

const ESTADOS: Array<{ value: string; label: string }> = [
  { value: 'SIN-SALIDA', label: 'Sin Salida' },
  { value: 'SIN-ENTRADA', label: 'Sin Entrada' },
  { value: 'SIN-ASIGNACION', label: 'Sin Asignación' },
  { value: 'SUPERA', label: 'Supera' },
];

const OPCIONES_EXCLUIR = ['MO', 'ADM'];

// Definición de chips a mostrar. Para "Nocturna" agrupamos HNOC (Nocturno) y HNOCM (Nocturno Mensualeros),
// que conceptualmente son la misma categoría y el backend separa en dos claves distintas.
const cupoLabels: Array<{ label: string; keys: Array<keyof CupoMarcacion> }> = [
  { label: 'Normal', keys: ['NORMA'] },
  { label: 'Nocturna', keys: ['HNOC', 'HNOCM'] },
  { label: 'Domingo', keys: ['DOMIN'] },
  { label: 'Domingo Noc.', keys: ['DONOC'] },
  { label: 'Feriado', keys: ['FERIA'] },
  { label: 'Feriado Noc.', keys: ['FENOC'] },
];

const sumarCupos = (cupos: CupoMarcacion, keys: Array<keyof CupoMarcacion>): string => {
  return keys.reduce<string>((acc, k) => sumarHorasHHMM(acc, cupos[k] ?? '0:00'), '0:00');
};

const formatName = (raw: string) => {
  const partes = String(raw || '').split(',');
  if (partes.length < 2) return raw;
  const [lastName, firstName] = partes;
  return `${firstName.trim()} ${lastName.trim()}`;
};

const toLocalISODate = (date: Date | undefined): string => {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};

const claseEstado = (estado: string) => {
  const e = String(estado || '').toUpperCase().trim();
  if (e === 'SIN-ENTRADA') return 'sin-entrada';
  if (e === 'SIN-SALIDA') return 'sin-salida';
  if (e === 'SUPERA') return 'supera';
  if (e === 'SIN-ASIGNACION') return 'sin-asignacion';
  return 'otro';
};

// Adapta un registro de la lista al formato que esperan los modales de SIN-ASIGNACION
// (Reemplazo rápido / Limpieza Profunda), que nacieron en la vista por Punto de Servicio.
const aDetalleSinAsignacion = (r: RegistroMarcacion): GestionErroresDetalleItem => ({
  tipo_error: 'SIN-ASIGNACION',
  ps_id: Number(r.psId),
  ps_nombre: r.nombrePServicio,
  operario_legajo: Number(r.nroLegajo),
  operario_nombre: r.nombre,
  fecha_hora: `${r.fecha} 00:00:00`,
  referencia_id: String(r.referencia_id || ''),
  detalle: `Hentrada=${r.entrada || ''} Hsalida=${r.salida || ''} TipoHora=${r.tipoHora || ''} comentario=SIN-ASIGNACION`,
});

interface Props {
  puntosServicio: string[];
  operarios: OperarioItem[];
  usuario: UsuarioInfo | null;
}

const PendientesPanel: React.FC<Props> = ({ puntosServicio, operarios, usuario }) => {
  const api = useGestionMarcacionApi();

  // Filtros
  const [puntoServicio, setPuntoServicio] = useState<string>('');
  const [fechaInicio, setFechaInicio] = useState<Date | undefined>(new Date());
  const [fechaFin, setFechaFin] = useState<Date | undefined>(new Date());
  const [operarioSelect, setOperarioSelect] = useState<OperarioItem | null>(null);
  const [estados, setEstados] = useState<string[]>([]);
  const [excluir, setExcluir] = useState<string[]>([]);

  // Datos
  const [registros, setRegistros] = useState<RegistroMarcacion[]>([]);
  const [cupos, setCupos] = useState<CupoMarcacion>({});
  const [yaFiltro, setYaFiltro] = useState(false);

  // UI
  const [loading, setLoading] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState<string>('');

  // Paginación
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;

  // Modales
  const [modal, setModal] = useState<null | 'marcacion' | 'supera'>(null);
  const [registroActivo, setRegistroActivo] = useState<RegistroMarcacion | null>(null);

  // SIN-ASIGNACION: selector (RR / LP) y modales de cada opción.
  const [rowSinAsignacion, setRowSinAsignacion] = useState<GestionErroresDetalleItem | null>(null);
  const [rowReemplazo, setRowReemplazo] = useState<GestionErroresDetalleItem | null>(null);
  const [rowLimpieza, setRowLimpieza] = useState<GestionErroresDetalleItem | null>(null);
  // SIN-ASIGNACION ya gestionados en esta sesión (reemplazo rápido), por referencia_id.
  // El registro sigue en la lista hasta el reproceso: así no se gestiona dos veces.
  const [sinAsigGestionados, setSinAsigGestionados] = useState<Record<string, boolean>>({});

  const sinAsignacionGestionado = (registro: RegistroMarcacion): boolean => {
    const ref = String(registro.referencia_id || '').trim();
    if (!ref) return false;
    if (sinAsigGestionados[ref]) return true;
    // Los enviados a Limpieza Profunda quedan marcados en sessionStorage (igual que en la vista por PS).
    try {
      return listarLpGestionados().some((m) => String(m?.referencia_id || '').trim() === ref);
    } catch {
      return false;
    }
  };

  const marcarSinAsigGestionado = (referenciaId: string) => {
    const ref = String(referenciaId || '').trim();
    if (ref) setSinAsigGestionados((prev) => ({ ...prev, [ref]: true }));
  };

  // Filtrar al fiscal logueado para que no se vea a sí mismo (igual que la vista vieja).
  // Y ordenar por fecha ascendente (las fechas vienen en formato YYYY-MM-DD desde el backend,
  // por lo que la comparación lexicográfica equivale a orden cronológico).
  const registrosVisibles = useMemo(() => {
    let lista = registros;
    if (usuario) {
      const esFiscal = String(usuario.cargo || '').toUpperCase().includes('FISCAL');
      if (esFiscal) {
        const nombreFormat = formatName(usuario.nombre);
        lista = registros.filter(
          (r) => r.nombre !== nombreFormat && Number(r.nroLegajo) !== Number(usuario.legajo)
        );
      }
    }
    return [...lista].sort((a, b) => String(a.fecha || '').localeCompare(String(b.fecha || '')));
  }, [registros, usuario]);

  const paginados = useMemo(() => {
    const start = page * rowsPerPage;
    return registrosVisibles.slice(start, start + rowsPerPage);
  }, [registrosVisibles, page, rowsPerPage]);

  const totalPaginas = Math.max(1, Math.ceil(registrosVisibles.length / rowsPerPage));

  const handleFiltrar = async () => {
    setLoading(true);
    setErrorGeneral('');
    setPage(0);
    const filtro = {
      fechaInicio: toLocalISODate(fechaInicio),
      fechaFin: toLocalISODate(fechaFin),
      operario: operarioSelect ? `${operarioSelect.nombre} - ${operarioSelect.legajo}` : '',
      // Estados se mandan concatenados por coma; el backend construye un IN(...) si hay más de uno.
      estado: estados.join(','),
      excluir: excluir,
      nombrePs: puntoServicio,
    };
    const res = await api.filtrarGestion(filtro);
    if (res.ok && res.data) {
      setRegistros(res.data.datos);
      setCupos(res.data.cupos);
      setYaFiltro(true);
    } else {
      setRegistros([]);
      setCupos({});
      setErrorGeneral(res.error || 'No se pudo obtener la información.');
    }
    setLoading(false);
  };

  const handleLimpiar = () => {
    setPuntoServicio('');
    setFechaInicio(new Date());
    setFechaFin(new Date());
    setOperarioSelect(null);
    setEstados([]);
    setExcluir([]);
    setRegistros([]);
    setCupos({});
    setYaFiltro(false);
    setErrorGeneral('');
    setPage(0);
  };

  const abrirAccion = (registro: RegistroMarcacion) => {
    const e = String(registro.estado || '').toUpperCase().trim();
    if (e === 'SIN-ASIGNACION') {
      setRowSinAsignacion(aDetalleSinAsignacion(registro));
      return;
    }
    setRegistroActivo(registro);
    if (e === 'SIN-ENTRADA' || e === 'SIN-SALIDA') setModal('marcacion');
    else if (e === 'SUPERA') setModal('supera');
  };

  const cerrarModal = () => {
    setModal(null);
    setRegistroActivo(null);
  };

  const refrescarTrasGestion = () => {
    // Re-ejecuta el filtro actual para reflejar el resultado.
    handleFiltrar();
  };

  const renderIconoAccion = (registro: RegistroMarcacion) => {
    const e = String(registro.estado || '').toUpperCase().trim();
    if (e === 'SIN-ENTRADA' || e === 'SIN-SALIDA') {
      return (
        <IconButton size="small" onClick={() => abrirAccion(registro)} style={{ color: '#900533' }} title="Gestionar">
          <CreateOutlinedIcon />
        </IconButton>
      );
    }
    if (e === 'SUPERA') {
      return (
        <IconButton size="small" onClick={() => abrirAccion(registro)} style={{ color: '#900533' }} title="Gestionar">
          <TrendingUpOutlinedIcon />
        </IconButton>
      );
    }
    // SIN-ASIGNACION: requiere el id del registro y del PS (los expone listar_gestion).
    if (e === 'SIN-ASIGNACION' && sinAsignacionGestionado(registro)) {
      return <span className="gmv2-estado" style={{ background: '#e8f5e9', color: '#2e7d32' }}>GESTIONADO</span>;
    }
    if (e === 'SIN-ASIGNACION' && registro.referencia_id && registro.psId) {
      return (
        <IconButton size="small" onClick={() => abrirAccion(registro)} style={{ color: '#900533' }} title="Gestionar">
          <AssignmentIndOutlinedIcon />
        </IconButton>
      );
    }
    return null;
  };

  return (
    <>
      {/* Filtros */}
      <div className="gmv2-card">
        <h3>Filtros</h3>
        <div className="gmv2-filtros-grid">
          <Autocomplete
            options={puntosServicio}
            value={puntoServicio || null}
            onChange={(_e, val) => setPuntoServicio(val || '')}
            getOptionLabel={(opt) => opt}
            renderInput={(params) => (
              <TextField {...params} label="Punto de Servicio" variant="outlined" size="small" />
            )}
          />

          <DatePicker
            placeholder="Fecha Inicio"
            value={fechaInicio}
            setValue={setFechaInicio}
          />

          <DatePicker
            placeholder="Fecha Fin"
            value={fechaFin}
            setValue={setFechaFin}
          />

          <Autocomplete
            options={operarios}
            value={operarioSelect}
            onChange={(_e, val) => setOperarioSelect(val as OperarioItem | null)}
            getOptionLabel={(opt: OperarioItem) => `${opt.nombre} - ${opt.legajo}`}
            renderInput={(params) => (
              <TextField {...params} label="Operario" variant="outlined" size="small" />
            )}
          />

          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            disableCloseOnSelect
            options={ESTADOS}
            value={ESTADOS.filter((opt) => estados.includes(opt.value))}
            onChange={(_e, val) => setEstados((val as Array<{ value: string; label: string }>).map((o) => o.value))}
            getOptionLabel={(opt) => opt.label}
            getOptionSelected={(opt, sel) => opt.value === sel.value}
            renderInput={(params) => (
              <TextField {...params} label="Estado" variant="outlined" size="small" />
            )}
          />

          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            disableCloseOnSelect
            options={OPCIONES_EXCLUIR}
            value={excluir}
            onChange={(_e, val) => setExcluir(val as string[])}
            renderInput={(params) => (
              <TextField {...params} label="Excluir" variant="outlined" size="small" />
            )}
          />
        </div>

        <div className="gmv2-acciones">
          <Button
            variant="outlined"
            onClick={handleLimpiar}
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            Limpiar
          </Button>
          <Button
            variant="contained"
            onClick={handleFiltrar}
            disabled={loading}
            style={{ backgroundColor: '#900533', color: 'white' }}
            startIcon={<SearchIcon />}
          >
            Filtrar
          </Button>
        </div>

        {errorGeneral && (
          <Box marginTop={1}>
            <Typography color="error" variant="body2">{errorGeneral}</Typography>
          </Box>
        )}
      </div>

      {/* Resumen de cupos */}
      {yaFiltro && (
        <div className="gmv2-card">
          <h3>Resumen de cupos</h3>
          <div className="gmv2-chips-cupos">
            {cupoLabels.map(({ label, keys }) => (
              <span key={label} className="gmv2-chip-cupo">
                <span className="label">{label}:</span>
                <span>{sumarCupos(cupos, keys)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Resultados */}
      {yaFiltro && (
        <div className="gmv2-card">
          <h3>Registros ({registrosVisibles.length})</h3>

          {registrosVisibles.length === 0 ? (
            <div className="gmv2-empty">
              No se encontraron registros para los filtros aplicados.
            </div>
          ) : (
            <>
              {paginados.map((registro, idx) => (
                <div key={`${registro.idMarcacion}-${idx}`} className="gmv2-registro">
                  <div className="gmv2-registro-head">
                    <div>
                      <div className="gmv2-registro-nombre">{registro.nombre}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Legajo {registro.nroLegajo} · {registro.nombrePServicio}
                      </div>
                    </div>
                    <Box display="flex" alignItems="center" gridGap={6}>
                      <span className={`gmv2-estado ${claseEstado(registro.estado)}`}>
                        {registro.estado}
                      </span>
                      {renderIconoAccion(registro)}
                    </Box>
                  </div>

                  <div className="gmv2-registro-meta">
                    <span><strong>Fecha:</strong> {registro.fecha}</span>
                    <span><strong>Entrada:</strong> {registro.entrada ?? '-'}</span>
                    <span><strong>Salida:</strong> {registro.salida ?? '-'}</span>
                    <span><strong>Tipo:</strong> {registro.tipoHora || '-'}</span>
                    <span><strong>Marcación:</strong> {registro.tipoMarcacion || '-'}</span>
                    <span><strong>Total:</strong> {registro.total ?? '-'}</span>
                  </div>
                </div>
              ))}

              <div className="gmv2-paginacion">
                <div className="gmv2-paginacion-row">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Anterior
                  </Button>
                  <span className="gmv2-paginacion-info">Página {page + 1} de {totalPaginas}</span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPage((p) => Math.min(totalPaginas - 1, p + 1))}
                    disabled={page >= totalPaginas - 1}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {!yaFiltro && !loading && (
        <div className="gmv2-card gmv2-empty">
          Aplicá filtros y presioná "Filtrar" para ver los registros.
        </div>
      )}

      {/* Loading overlay */}
      <Backdrop open={loading} style={{ zIndex: 1300, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Modales */}
      <CorregirMarcacionModal
        open={modal === 'marcacion'}
        registro={registroActivo}
        onClose={cerrarModal}
        onSuccess={refrescarTrasGestion}
      />
      <SuperaModal
        open={modal === 'supera'}
        registro={registroActivo}
        onClose={cerrarModal}
        onSuccess={refrescarTrasGestion}
      />

      <GestionSinAsignacionSelectorModal
        isOpen={!!rowSinAsignacion}
        onClose={() => setRowSinAsignacion(null)}
        onElegirReemplazoRapido={() => {
          setRowReemplazo(rowSinAsignacion);
          setRowSinAsignacion(null);
        }}
        onElegirLimpiezaProfunda={() => {
          setRowLimpieza(rowSinAsignacion);
          setRowSinAsignacion(null);
        }}
      />
      <ReemplazoRapidoModal
        isOpen={!!rowReemplazo}
        row={rowReemplazo}
        onClose={() => setRowReemplazo(null)}
        onSuccess={() => {
          if (rowReemplazo) marcarSinAsigGestionado(rowReemplazo.referencia_id);
          refrescarTrasGestion();
        }}
      />
      <AsignarLimpiezaProfundaModal
        isOpen={!!rowLimpieza}
        row={rowLimpieza}
        onClose={() => setRowLimpieza(null)}
        onAssigned={({ referencia_id, ps_id, fecha, operario_legajo }) => {
          // Misma marca que usa la vista por PS para descontar el caso de su contador.
          agregarLpGestionado({ referencia_id, ps_id, fecha, operario_legajo });
          marcarSinAsigGestionado(referencia_id);
          refrescarTrasGestion();
        }}
      />
    </>
  );
};

export default PendientesPanel;
